/**
 * Shared visit-time parsing + duration helpers (#2017).
 *
 * Two different parsers used to live side by side: StoreDetail's lenient
 * `parseTimeToDate` (accepts ISO, Postgres space format, bare "HH:MM[:SS]",
 * and "h:mm AM/PM") and CheckoutSummary's strict `safeDate` (only what
 * `new Date()` parses — i.e. full datetimes). When a calllog list read handed
 * back a TIME-ONLY `stcalltime` (the serializer's `str(value)` fallback for a
 * non-datetime value), the lenient parser accepted it for the store-time math
 * while the strict one rejected it for the editable picker — so the Leave
 * Store summary showed a BLANK check-in field next to a nonsensical negative
 * "Total Store Time". This module is the single source of truth so the two
 * never disagree again.
 */

/**
 * Lenient time parser. Accepts, in order:
 *   - any string `new Date()` understands (full ISO, Postgres space format)
 *   - bare 24-hour "HH:MM" / "HH:MM:SS"           -> today at that time
 *   - 12-hour "h:mm AM/PM"                          -> today at that time
 * Returns null for empty/unparseable input.
 */
export function parseFlexibleTime(time: string | null | undefined): Date | null {
  if (!time) return null;

  // Full date/datetime first (ISO, Postgres "YYYY-MM-DD HH:MM:SS+00:00").
  const date = new Date(time);
  if (!isNaN(date.getTime())) return date;

  // "h:mm AM/PM" / "hh:mm AM/PM"
  const amPm = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (amPm) {
    let hours = parseInt(amPm[1], 10);
    const minutes = parseInt(amPm[2], 10);
    const isPM = amPm[3].toUpperCase() === 'PM';
    if (isPM && hours !== 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;
    const today = new Date();
    today.setHours(hours, minutes, 0, 0);
    return today;
  }

  // "HH:MM" / "HH:MM:SS" (24-hour)
  const hhmm = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (hhmm) {
    const hours = parseInt(hhmm[1], 10);
    const minutes = parseInt(hhmm[2], 10);
    if (hours > 23 || minutes > 59) return null;
    const today = new Date();
    today.setHours(hours, minutes, 0, 0);
    return today;
  }

  return null;
}

/**
 * True only when the value carries a real DATE (full ISO / Postgres format) —
 * i.e. `new Date()` can parse it on its own. Time-only strings are false, so
 * callers can tell an authoritative server timestamp from a lossy time-of-day
 * and prefer a clean local timestamp instead. (#2017)
 */
export function isFullDateTime(time: string | null | undefined): boolean {
  if (!time) return false;
  return !isNaN(new Date(time).getTime());
}

/** Normalize any recognizable time to an ISO string, or '' when unparseable. */
export function flexibleTimeToISO(time: string | null | undefined): string {
  const d = parseFlexibleTime(time);
  return d ? d.toISOString() : '';
}

/**
 * Format any recognizable time as a NAIVE local wall-clock string
 * ("YYYY-MM-DDTHH:MM:SS", no Z/offset) — the calllog serializer's documented
 * contract (it localizes a naive wall-clock to the rep/store tz). A UTC ISO
 * (".toISOString()", "…Z") would be re-stamped into that tz and land +offset
 * hours off, so every submitted time on a visit must share this one convention
 * or the check-in/out pair skews by the device offset (the diag #45 class). A
 * value already in naive-local form is returned unchanged (idempotent); an
 * unparseable value passes through so the server can anchor/report it.
 */
export function toNaiveLocal(time: string | null | undefined): string {
  if (!time) return time ?? '';
  const d = parseFlexibleTime(time);
  if (!d) return time;
  const m = roundToMinute(d);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${m.getFullYear()}-${p(m.getMonth() + 1)}-${p(m.getDate())}T${p(m.getHours())}:${p(m.getMinutes())}:00`;
}

/**
 * The minute a timestamp is recorded as (#3452).
 *
 * A minute is the grain the business actually works in: the weekly timesheet
 * totals minutes, per-hour billing derives quarter-hours, pay is hourly, and
 * the commute rule deducts 30 minutes. Nothing downstream is denominated in
 * seconds, so recording them is precision the business never uses — and it
 * costs: a drive whose endpoints landed in the same second reached the server
 * as `stdrivetime == eddrivetime` and was rejected outright, killing the
 * check-in. Check-in/out times were ALREADY stored minute-truncated; drive
 * times were the odd one out, and this makes them agree.
 *
 * ROUNDING POLICY — round to nearest, so errors cancel across a day's legs
 * rather than accumulating. Truncating would systematically under-pay the rep;
 * always rounding out would systematically inflate paid time. This is a
 * PLACEHOLDER pending Beth/James, in the same bucket as the commute rule:
 * change this one function to change the policy.
 */
export function roundToMinute(value: Date): Date {
  const rounded = new Date(value.getTime());
  rounded.setSeconds(rounded.getSeconds() + 30, 0);
  rounded.setSeconds(0, 0);
  return rounded;
}

/** The two instants as the MINUTES they will be recorded as. */
export function sameRecordedMinute(a: Date, b: Date): boolean {
  return roundToMinute(a).getTime() === roundToMinute(b).getTime();
}

/**
 * #2679: Expand a bare wall-clock time ("10:18", "1:26 PM") to a full naive-local
 * datetime anchored to the DATE of `anchorIso` (a full datetime on the SAME visit,
 * e.g. the session's start time) — NOT to "today" like parseFlexibleTime. A
 * manual-entry session can persist a clock-only end time; sent raw to
 * /api/projects/calllog/bulk/, the server's datetime.fromisoformat() rejects it
 * and 400s the whole visit. Full datetimes, unrecognized values, and anchor-less
 * bare times pass through unchanged (the server coerces/reports those).
 */
export function toWireDateTime(
  value: string | null | undefined,
  anchorIso?: string | null,
): string | undefined {
  if (!value) return value ?? undefined;
  if (isFullDateTime(value)) return value; // already carries a date

  const s = value.trim();
  const amPm = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  const hhmm = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  let hours: number;
  let minutes: number;
  let seconds = '00';
  if (amPm) {
    hours = parseInt(amPm[1], 10);
    minutes = parseInt(amPm[2], 10);
    if (amPm[3].toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (amPm[3].toUpperCase() === 'AM' && hours === 12) hours = 0;
  } else if (hhmm) {
    hours = parseInt(hhmm[1], 10);
    minutes = parseInt(hhmm[2], 10);
    if (hhmm[3]) seconds = hhmm[3];
  } else {
    return value; // unrecognized shape
  }
  if (hours > 23 || minutes > 59) return value;

  const datePart = anchorIso && isFullDateTime(anchorIso) ? anchorIso.slice(0, 10) : null;
  if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return value; // no date to anchor
  return `${datePart}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${seconds}`;
}

/**
 * Minutes between check-in and check-out, never negative. Falls back to the
 * upstream task total when either timestamp is missing/unparseable, and clamps
 * that fallback to >= 0 as well — so the summary can never render "-60m". (#2017)
 */
export function clampStoreMinutes(
  checkInTime: string | null | undefined,
  checkOutTime: string | null | undefined,
  fallbackMinutes: number,
): number {
  const start = parseFlexibleTime(checkInTime);
  const end = parseFlexibleTime(checkOutTime);
  if (!start || !end) return Math.max(0, fallbackMinutes);
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
}
